import { useEffect, useState } from "react";
import { fetchAbsences, fetchUsers, send } from "../api.js";
import type { AbsenceRange, AppUser, Board } from "../api.js";

/**
 * People: who is on which team, what they may do, and what the solver reads.
 *
 * Three different notions of "role" meet on this screen, so the columns are
 * named apart: `Craft` is the job title the solver's eligibility rules read
 * (resource.role), `Team` is membership, and `Access` is the permission level
 * (app_user.role). Calling any two of them "role" is how someone grants admin
 * while meaning to record that a person drafts.
 */
export function People(
  { board, reload, onViewAs }: { board: Board; reload: () => void; onViewAs?: (resourceId: string) => void },
) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pto, setPto] = useState<{ id: string; from: string; to: string; half?: boolean; replacing?: AbsenceRange } | null>(null);
  const [absences, setAbsences] = useState<AbsenceRange[]>([]);
  const [showPast, setShowPast] = useState(false);
  const [newName, setNewName] = useState("");

  const pressure = new Map(board.pressure.map((p) => [p.resourceId, p.pct]));

  const loadAbsences = async () => {
    try { setAbsences((await fetchAbsences()).absences); }
    catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void loadAbsences(); }, [board]);

  const [users, setUsers] = useState<AppUser[]>([]);
  const isAdmin = board.user.role === "admin";
  const canEdit = (r: { teamId: string | null }) =>
    isAdmin || (r.teamId !== null && board.user.managedTeamIds.includes(r.teamId));

  const loadUsers = async () => {
    try { setUsers((await fetchUsers()).users); }
    catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void loadUsers(); }, [board]);

  const accessOf = (email: string | null): AppUser["role"] =>
    (email && users.find((u) => u.email?.toLowerCase() === email.toLowerCase())?.role) || "engineer";

  async function setAccess(email: string, role: AppUser["role"]) {
    setBusy(email); setErr(null);
    try { await send("/users", "PATCH", { email, role }); await loadUsers(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }

  async function setAccessByUserId(userId: string, role: AppUser["role"]) {
    setBusy(userId); setErr(null);
    try { await send("/users", "PATCH", { userId, role }); await loadUsers(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }

  async function patch(id: string, body: unknown) {
    setBusy(id); setErr(null);
    try { await send(`/resources/${id}`, "PATCH", body); reload(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }

  async function addPerson() {
    const name = newName.trim();
    // Guards against the same submit landing twice — Enter and a click on
    // "Add person" both call this, and disabling the button only takes effect
    // on the next paint, which is not always ahead of a second, fast input.
    if (!name || busy === "new") return;
    // Cleared immediately, before the request even lands, so the box itself
    // is the confirmation that the click was received — waiting for the
    // response to clear it left a window where "nothing happened yet" and
    // "already added" looked identical, and a second Enter or click in that
    // window resubmitted the same name.
    setNewName("");
    setBusy("new"); setErr(null);
    try { await send("/resources", "POST", { name }); reload(); }
    catch (e) { setErr((e as Error).message); setNewName(name); }
    finally { setBusy(null); }
  }

  async function saveTimeOff(remove = false) {
    if (!pto) return;
    setBusy(pto.id); setErr(null);
    try {
      // Changing a booking is a clear followed by a book. The old range has to
      // go first, or shortening one would leave the days it used to cover
      // still booked and invisible.
      if (pto.replacing && !remove) {
        await send("/absences", "DELETE", {
          resourceId: pto.replacing.resourceId,
          from: pto.replacing.from,
          to: pto.replacing.to,
        });
      }
      await send(`/resources/${pto.id}/availability`, "POST", {
        from: pto.from, to: pto.to, availableHalfDays: pto.half ? 1 : 0, reason: "PTO", remove,
      });
      setPto(null); await loadAbsences(); reload();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }

  async function removeRange(a: AbsenceRange) {
    if (!confirm(
      `Remove ${a.resourceName}'s time off, ${a.from}${a.to !== a.from ? ` to ${a.to}` : ""}?` +
      (a.heldBy ? `\n\nIt was booked by “${a.heldBy.label}” in the scenario “${a.heldBy.scenarioName}”, which will still claim it.` : ""),
    )) return;
    setBusy(a.resourceId); setErr(null);
    try {
      await send("/absences", "DELETE", { resourceId: a.resourceId, from: a.from, to: a.to });
      await loadAbsences(); reload();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(null); }
  }

  return (
    <div>
      <Teams board={board} reload={reload} />

      <p className="hint" style={{ marginTop: 0, marginBottom: 12 }}>
        These three fields are what the scheduler and the solver actually read.
        Capacity is the share of an 8-hour day that gets planned — 80% leaves room for
        interruptions. Time off removes days entirely rather than penalizing them.
      </p>
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}
      <div className="scroll tall">
        <table className="qt" style={{ minWidth: 1180 }}>
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Team</th><th>Craft</th><th>Access</th>
              <th>FTE</th><th>Capacity</th>
              <th className="c">4wk pressure</th><th>Active</th><th>Time off</th>
              {onViewAs && <th></th>}
            </tr>
          </thead>
          <tbody>
            {board.resources.filter((r) => !r.isPool).map((r) => {
              const p = pressure.get(r.resourceId) ?? 0;
              return (
                <tr key={r.resourceId} style={{ opacity: busy === r.resourceId ? 0.5 : 1 }}>
                  <td>
                    {isAdmin ? (
                      <input
                        key={r.name} style={{ width: 160, fontWeight: 600 }} defaultValue={r.name}
                        aria-label={`Name of ${r.name}`}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (!v) { e.target.value = r.name; return; }
                          if (v !== r.name) void patch(r.resourceId, { name: v });
                        }}
                      />
                    ) : <b>{r.name}</b>}
                  </td>
                  <td>
                    <input
                      className="num" style={{ width: 180 }} type="email"
                      defaultValue={r.email ?? ""} placeholder="not linked"
                      disabled={!isAdmin}
                      onBlur={(e) => {
                        const v = e.target.value.trim().toLowerCase();
                        if (v !== (r.email ?? "")) void patch(r.resourceId, { email: v || null });
                      }}
                    />
                  </td>
                  <td>
                    <select
                      value={r.teamId ?? ""} disabled={!isAdmin}
                      onChange={(e) => void patch(r.resourceId, { teamId: e.target.value || null })}
                    >
                      <option value="">— no team —</option>
                      {board.teams.map((t) => (
                        <option key={t.teamId} value={t.teamId}>{t.name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={r.role ?? ""} disabled={!canEdit(r)}
                      onChange={(e) => void patch(r.resourceId, { role: e.target.value || null })}
                    >
                      <option value="">— none —</option>
                      <option>Designer</option><option>Engineer</option>
                      <option>ME</option><option>Checker</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={accessOf(r.email)} disabled={!isAdmin || !r.email}
                      title={r.email
                        ? undefined
                        : "Give this person an email address first — it is the link to their account."}
                      onChange={(e) => void setAccess(r.email!, e.target.value as AppUser["role"])}
                    >
                      <option value="engineer">Engineer</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td>
                    <input className="num" type="number" step="0.1" min="0.1" max="1" defaultValue={r.fte}
                      disabled={!canEdit(r)}
                      onBlur={(e) => { const v = Number(e.target.value); if (v !== r.fte) void patch(r.resourceId, { fte: v }); }} />
                  </td>
                  <td>
                    <input className="num" type="number" step="5" min="10" max="150" defaultValue={r.capacityPct}
                      disabled={!canEdit(r)}
                      onBlur={(e) => { const v = Number(e.target.value); if (v !== r.capacityPct) void patch(r.resourceId, { capacityPct: v }); }} />
                    <span className="muted"> %</span>
                  </td>
                  <td className={`c mono ${p > 1 ? "crit" : p < 0.4 ? "muted" : ""}`}>{Math.round(p * 100)}%</td>
                  <td>
                    <input type="checkbox" checked={r.active} disabled={!canEdit(r)}
                      onChange={(e) => void patch(r.resourceId, { active: e.target.checked })} />
                  </td>
                  <td>
                    <button className="btn" disabled={!canEdit(r)}
                      onClick={() => setPto({ id: r.resourceId, from: board.today, to: board.today })}>
                      Add
                    </button>
                  </td>
                  {onViewAs && (
                    <td>
                      {isAdmin && !board.user.viewingAs && (
                        <button className="btn" title={`See the board as ${r.name} would`}
                          onClick={() => onViewAs(r.resourceId)}>
                          View as
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isAdmin && (
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input
            placeholder="New person's name" value={newName} disabled={busy === "new"}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && newName.trim()) void addPerson(); }}
          />
          <button className="btn on" disabled={!newName.trim() || busy === "new"} onClick={() => void addPerson()}>
            Add person
          </button>
        </div>
      )}

      <div className="run-sec">
        <h3>
          Booked time off
          <span className="run-sec-note">
            {absences.filter((a) => showPast || !a.past).length} booking
            {absences.filter((a) => showPast || !a.past).length === 1 ? "" : "s"}
          </span>
        </h3>
        {!absences.length ? (
          <p className="muted">Nobody has time off booked.</p>
        ) : (
          <>
            <div className="scroll">
              <table className="qt" style={{ minWidth: 780 }}>
                <thead>
                  <tr>
                    <th>Who</th><th>From</th><th>To</th><th className="r">Days</th>
                    <th>Kind</th><th>Reason</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {absences.filter((a) => showPast || !a.past).map((a) => (
                    <tr key={`${a.resourceId}|${a.from}`}
                      style={{ opacity: busy === a.resourceId ? 0.5 : a.past ? 0.55 : 1 }}>
                      <td><b>{a.resourceName}</b></td>
                      <td className="mono">{a.from}</td>
                      <td className="mono">{a.to !== a.from ? a.to : <span className="muted">—</span>}</td>
                      <td className="r">{a.days}</td>
                      <td>{a.halfDays === 0 ? "full day" : a.halfDays === 1 ? "half day" : `${a.halfDays} half-day`}</td>
                      <td>
                        {a.reason ?? "—"}
                        {a.heldBy && (
                          <span className="chip draft"
                            title={`Booked by “${a.heldBy.label}” in the scenario “${a.heldBy.scenarioName}”`}>
                            from a scenario
                          </span>
                        )}
                      </td>
                      <td>
                        <button className="btn" disabled={!!busy}
                          onClick={() => setPto({ id: a.resourceId, from: a.from, to: a.to, half: a.halfDays === 1, replacing: a })}>
                          Change
                        </button>
                        <button className="btn" disabled={!!busy}
                          onClick={() => void removeRange(a)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {absences.some((a) => a.past) && (
              <label className="muted" style={{ fontSize: 12, display: "inline-flex", gap: 6, marginTop: 8 }}>
                <input type="checkbox" checked={showPast}
                  onChange={(e) => setShowPast(e.target.checked)} />
                show time off that has already passed ({absences.filter((a) => a.past).length})
              </label>
            )}
          </>
        )}
      </div>

      <Unlinked users={users} isAdmin={isAdmin} onSetRole={(userId, role) => void setAccessByUserId(userId, role)} />

      {pto && (
        <div className="modal">
          <div className="modal-card">
            <h3>
              {pto.replacing ? "Change time off" : "Time off"} — {board.resources.find((r) => r.resourceId === pto.id)?.name}
            </h3>
            <p className="muted" style={{ fontSize: 13 }}>
              Weekends are skipped automatically. Every phase after this in that person's
              queue shifts, so publish is where you see the cost.
            </p>
            {workingDaysBetween(pto.from, pto.to) === 0 && (
              <div className="err" style={{ marginBottom: 10 }}>
                That range is all weekend — it would book nothing.
              </div>
            )}
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <label>from<input type="date" value={pto.from} onChange={(e) => setPto({ ...pto, from: e.target.value })} /></label>
              <label>to<input type="date" value={pto.to} onChange={(e) => setPto({ ...pto, to: e.target.value })} /></label>
            </div>
            {/* Plain row, not the dialog's small-caps field style, which is for the date fields above. */}
            <label style={{
              flexDirection: "row", alignItems: "center", gap: 8, marginTop: 14,
              fontFamily: "inherit", fontSize: 13, letterSpacing: "normal", textTransform: "none", color: "var(--ink)",
            }}>
              <input type="checkbox" checked={!!pto.half} onChange={(e) => setPto({ ...pto, half: e.target.checked })} />
              Half day
            </label>
            <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }}>
              Books half a day off for each day in the range. It does not matter which half.
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button className="btn on" disabled={!!busy} onClick={() => void saveTimeOff(false)}>
                {pto.replacing ? "Save the change" : "Book time off"}
              </button>
              <button className="btn" disabled={!!busy} onClick={() => void saveTimeOff(true)}>
                Clear this range
              </button>
              <span className="spacer" />
              <button className="btn" onClick={() => setPto(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Working days in a range, so the modal can refuse a weekend before it is sent
 * rather than after. The server refuses it too; this is so the reader does not
 * have to submit to find out.
 */
function workingDaysBetween(from: string, to: string): number {
  if (!from) return 0;
  let n = 0;
  const cursor = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to || from}T00:00:00Z`);
  while (cursor <= end && n < 400) {
    const dow = cursor.getUTCDay();
    if (dow >= 1 && dow <= 5) n++;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return n;
}

/**
 * A team's project types: the ones it has as removable chips, and a list of the
 * rest to add from. A plain text box with a datalist could not do this — a
 * browser narrows a datalist to whatever is already typed, so once a type was
 * chosen the list shrank to that one type and the others were unreachable.
 * "Other…" is for a type that is not in the data yet.
 */
function TypePicker({ value, options, disabled, onChange }: {
  value: string[]; options: string[]; disabled: boolean; onChange: (next: string[]) => void;
}) {
  const norm = (t: string) => t.trim().toUpperCase();
  const have = new Set(value.map(norm));
  const addable = options.filter((o) => !have.has(norm(o)));
  const add = (t: string) => {
    const v = t.trim();
    if (v && !have.has(norm(v))) onChange([...value, v]);
  };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", minWidth: 200 }}>
      {value.map((t) => (
        <span key={t} className="chip std" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          {t}
          {!disabled && (
            <button
              type="button" aria-label={`Remove ${t}`} title={`Remove ${t}`}
              style={{ all: "unset", cursor: "pointer", lineHeight: 1 }}
              onClick={() => onChange(value.filter((x) => norm(x) !== norm(t)))}
            >×</button>
          )}
        </span>
      ))}
      {!value.length && disabled && <span className="muted">—</span>}
      {!disabled && (
        <select
          value="" aria-label="Add a project type"
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__other") {
              const typed = prompt("Project type, exactly as Epicor shows it (e.g. SSYS):");
              if (typed) add(typed);
            } else if (v) add(v);
          }}
        >
          <option value="">{value.length ? "Add another…" : "Add a type…"}</option>
          {addable.map((o) => <option key={o} value={o}>{o}</option>)}
          <option value="__other">Other…</option>
        </select>
      )}
    </div>
  );
}

function Teams({ board, reload }: { board: Board; reload: () => void }) {
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const isAdmin = board.user.role === "admin";
  const people = board.resources.filter((r) => !r.isPool);
  const pool = board.resources.filter((r) => r.isPool);
  const size = (teamId: string) => people.filter((r) => r.teamId === teamId).length;
  // Every type that exists: on a project Epicor sent, or already given to a team.
  const knownTypes = [...new Set([
    ...board.projects.map((p) => p.productCode),
    ...board.teams.flatMap((t) => t.projectTypes ?? []),
  ].map((c) => (c ?? "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const run = async (fn: () => Promise<unknown>) => {
    setErr(null);
    try { await fn(); reload(); } catch (e) { setErr((e as Error).message); }
  };

  return (
    <div className="run-sec">
      <h3>Teams<span className="run-sec-n">{board.teams.length}</span></h3>
      <p className="hint" style={{ marginTop: 0 }}>
        A manager has full edit rights over their own team — its people, and the work
        assigned to them. They cannot move work out to another team; that is an admin's.
        Give a team <b>project types</b> (the type Epicor sends with each project) and its
        manager also owns every project of those types: they can plan and reassign that
        work, add, remove and reorder its steps, and publish a draft that only touches it.
      </p>
      {err && <div className="err" style={{ marginBottom: 10 }}>{err}</div>}
      <table className="qt" style={{ minWidth: 620 }}>
        <thead><tr><th>Team</th><th>Manager</th><th>Project types</th><th className="r">People</th><th /></tr></thead>
        <tbody>
          {board.teams.map((t) => (
            <tr key={t.teamId}>
              <td>
                <input
                  className="num" style={{ width: 200 }} defaultValue={t.name} disabled={!isAdmin}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== t.name) void run(() => send(`/teams/${t.teamId}`, "PATCH", { name: v }));
                  }}
                />
              </td>
              <td>
                {/*
                  Every non-pool person, not just this team's members: a manager
                  is often a supervisor who drafts nothing and holds no work of
                  their own, so requiring membership would break that common
                  case to guard against a misconfiguration an admin has to make
                  on purpose.
                */}
                <select
                  value={t.managerResourceId ?? ""} disabled={!isAdmin}
                  onChange={(e) => void run(() =>
                    send(`/teams/${t.teamId}`, "PATCH", { managerResourceId: e.target.value || null }))}
                >
                  <option value="">— none —</option>
                  {people.map((r) => <option key={r.resourceId} value={r.resourceId}>{r.name}</option>)}
                </select>
              </td>
              <td>
                <TypePicker
                  value={t.projectTypes ?? []} options={knownTypes} disabled={!isAdmin}
                  onChange={(next) => void run(() => send(`/teams/${t.teamId}`, "PATCH", { projectTypes: next }))}
                />
              </td>
              <td className="r mono">{size(t.teamId)}</td>
              <td>
                {isAdmin && (
                  <button
                    className="btn"
                    onClick={() => {
                      if (!confirm(`Delete the team “${t.name}”?`)) return;
                      void run(() => send(`/teams/${t.teamId}`, "DELETE"));
                    }}
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {isAdmin && (
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input
            placeholder="New team name" value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && name.trim()) {
                void run(() => send("/teams", "POST", { name: name.trim() })).then(() => setName(""));
              }
            }}
          />
          <button
            className="btn on" disabled={!name.trim()}
            onClick={() => void run(() => send("/teams", "POST", { name: name.trim() }))
              .then(() => setName(""))}
          >
            Add team
          </button>
        </div>
      )}
      {isAdmin && pool.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ marginBottom: 4 }}>Pool resources</h3>
          <p className="hint" style={{ marginTop: 0 }}>
            Shared capacity, not a named person — left out of the roster above and out of
            each team's People count, but still a real member of whatever team it's set to.
            A team with none of these showing "0 people" can still refuse deletion until one
            of these is moved off it.
          </p>
          <table className="qt" style={{ minWidth: 420 }}>
            <thead><tr><th>Pool</th><th>Team</th></tr></thead>
            <tbody>
              {pool.map((r) => (
                <tr key={r.resourceId}>
                  <td>{r.name}</td>
                  <td>
                    <select
                      value={r.teamId ?? ""}
                      onChange={(e) => void run(() =>
                        send(`/resources/${r.resourceId}`, "PATCH", { teamId: e.target.value || null }))}
                    >
                      <option value="">— no team —</option>
                      {board.teams.map((t) => (
                        <option key={t.teamId} value={t.teamId}>{t.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Accounts that match nobody on the board.
 *
 * Without this, a new hire signs in, finds an empty My-work screen, and nobody
 * can tell why. The fix is one email address, and this is where you see that
 * it is missing.
 *
 * Access is still editable here even though the email fix above is not — a
 * platform admin or director often needs Sentry admin without ever doing
 * scheduled work, so they may sit here indefinitely on purpose. This is also
 * the only place to grant access to an account with no email at all: a
 * Google or password sign-in carries no trusted address (see
 * upsertAppUserByEmail), so the People table's Access column — keyed on
 * email — can never reach it. Set by user_id instead, which this account
 * already has because it has signed in at least once; an account that has
 * never signed in and has no email either names nobody yet, and there is
 * nothing to attach a role to until one of those exists.
 */
function Unlinked(
  { users, isAdmin, onSetRole }: { users: AppUser[]; isAdmin: boolean; onSetRole: (userId: string, role: AppUser["role"]) => void },
) {
  const orphans = users.filter((u) => !u.resourceId && !u.pending);
  if (!orphans.length) return null;
  return (
    <div className="run-sec">
      <h3>Signed in, not yet linked<span className="run-sec-n">{orphans.length}</span></h3>
      <p className="hint" style={{ marginTop: 0 }}>
        These accounts match no email address above, so they have no work of their own.
        Put the address on the right person and the link is made — or leave someone
        here on purpose and just set their access, if they hold no work of their own.
      </p>
      <table className="qt">
        <thead><tr><th>Email</th><th>Access</th></tr></thead>
        <tbody>
          {orphans.map((u) => (
            <tr key={u.userId}>
              <td className="mono">{u.email ?? "— (no trusted email on file)"}</td>
              <td>
                {isAdmin ? (
                  <select value={u.role} onChange={(e) => onSetRole(u.userId, e.target.value as AppUser["role"])}>
                    <option value="engineer">Engineer</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                ) : u.role}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
